import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;

function canRunUpdateCheck() {
  if (!('serviceWorker' in navigator)) return false;

  // Avoid doing background update work when the browser has explicitly asked
  // pages to save data. Visitors on constrained connections can still pick up
  // the next version on their next normal navigation or refresh.
  return navigator.connection?.saveData !== true;
}

export default function UpdateNotification() {
  const [registration, setRegistration] = useState(null);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, nextRegistration) {
      setRegistration(nextRegistration ?? null);
    },
  });

  useEffect(() => {
    if (!registration || !canRunUpdateCheck()) return undefined;

    const checkForUpdate = () => {
      if (document.visibilityState === 'visible') {
        registration.update();
      }
    };

    // Check right away instead of waiting for Workbox's next natural update
    // cycle, then keep checking while the tab is open. This keeps the site on
    // the newest deployed route/content graph before a visitor wanders into
    // removed links or stale content.
    checkForUpdate();
    const intervalId = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkForUpdate);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', checkForUpdate);
    };
  }, [registration]);

  useEffect(() => {
    if (needRefresh) {
      updateServiceWorker(true);
    }
  }, [needRefresh, updateServiceWorker]);

  return null;
}
